/**
 * AuthorizationGate
 *
 * A component that conditionally renders its children based on the current
 * user's allowed actions. Mirrors the backend `require_actions` decorator.
 *
 * Usage:
 *
 * ```tsx
 * // Simple — require a single action
 * <AuthorizationGate actions={['create_organization']}>
 *   <CreateOrgButton />
 * </AuthorizationGate>
 *
 * // AND — require ALL actions
 * <AuthorizationGate actions={['view_organization', 'view_members']} mode="all">
 *   <OrgDetails />
 * </AuthorizationGate>
 *
 * // OR — require ANY action
 * <AuthorizationGate actions={['edit_organization', 'delete_organization']} mode="any">
 *   <ManageOrg />
 * </AuthorizationGate>
 *
 * // With a fallback shown when not authorized
 * <AuthorizationGate actions={['manage_members']} fallback={<p>Not allowed</p>}>
 *   <MemberList />
 * </AuthorizationGate>
 * ```
 */

import React from 'react'
import { useAuthorization } from './AuthorizationContext'

interface AuthorizationGateProps {
    /** The action(s) required to render children. */
    actions: string[]
    /**
     * How to evaluate multiple actions:
     * - `'all'` (default) — user must have ALL specified actions (AND)
     * - `'any'` — user must have at least ONE specified action (OR)
     */
    mode?: 'all' | 'any'
    /** Content to render when the user IS authorized */
    children: React.ReactNode
    /** Optional content to render when the user is NOT authorized. Defaults to null (render nothing). */
    fallback?: React.ReactNode
}

export function AuthorizationGate({
    actions: requiredActions,
    mode = 'all',
    children,
    fallback = null,
}: AuthorizationGateProps) {
    const { hasAllActions, hasAnyAction, isLoading } = useAuthorization()

    // While loading, don't render anything to avoid flicker
    if (isLoading) return null

    const authorized =
        mode === 'all'
            ? hasAllActions(...requiredActions)
            : hasAnyAction(...requiredActions)

    return <>{authorized ? children : fallback}</>
}
